<template>
    <fullscreen-drawer
        v-if="featureFlags['live-site-responsive-design']"
        :visible="visible"
        :wrap-style="wrapStyle"
        height="100%"
        get-container="#app"
        placement="bottom"
        @close="handleClose"
    >
        <g-row class="g-mt-8">
            <g-column>
                <g-typography tag="h2" font-weight="semibold" text-align="center" class="g-mb-6">
                    Edit a Payout Method
                </g-typography>

                <g-typography tag="p" class="g-mb-3">
                    Visit your GRIN Pay dashboard to view your balance and more.
                </g-typography>

                <g-typography tag="p" class="g-mb-3">What you can do:</g-typography>

                <g-typography tag="p" class="g-mb-3">
                    <g-icon name="check-circle" color="grey-5" type="solid" />
                    View your balance and transactions
                </g-typography>

                <g-typography tag="p" class="g-mb-3">
                    <g-icon name="check-circle" color="grey-5" type="solid" />
                    Download your statements
                </g-typography>

                <g-typography tag="p" class="g-mb-5">
                    <g-icon name="check-circle" color="grey-5" type="solid" />
                    Manage your account settings
                </g-typography>

                <g-button
                    theme="live"
                    shape="round"
                    size="large"
                    :loading="isLinkLoading"
                    :disabled="isLinkLoading"
                    @click="handleRedirectToDashboard()"
                >
                    Go to GRIN Pay Dashboard
                </g-button>

                <g-divider margin="large" />
            </g-column>
        </g-row>

        <g-row type="flex" justify="space-between" class="g-mb-5">
            <g-column>
                <g-typography tag="h4" font-weight="label">
                    {{ DEFAULT_PAYOUT_METHOD_LABEL_STRING }}
                </g-typography>
            </g-column>

            <g-column>
                <g-switch
                    v-model="isDefault"
                    :loading="isSubmitting"
                    :disabled="isSubmitting"
                    @change="handleUpdateDefault"
                >
                    <g-icon slot="checkedChildren" name="check" size="xsmall" />
                    <g-icon slot="unCheckedChildren" name="times" size="xsmall" />
                </g-switch>
            </g-column>
        </g-row>

        <g-row>
            <g-column>
                <g-typography tag="p" class="g-mb-3">
                    {{ DEFAULT_PAYOUT_METHOD_GRIN_PAY_LINE_1_STRING }}
                </g-typography>

                <g-typography tag="p">
                    {{ DEFAULT_PAYOUT_METHOD_GRIN_PAY_LINE_2_STRING }}
                </g-typography>
            </g-column>
        </g-row>
    </fullscreen-drawer>

    <g-drawer
        v-else
        :visible="visible"
        :wrap-style="wrapStyle"
        height="100%"
        get-container="#app"
        placement="bottom"
        @close="handleClose"
    >
        <g-row class="g-mt-8">
            <g-column>
                <g-typography tag="h2" font-weight="semibold" text-align="center" class="g-mb-6">
                    Edit a Payout Method
                </g-typography>

                <g-typography tag="p" class="g-mb-3">
                    Visit your GRIN Pay dashboard to view your balance and more.
                </g-typography>

                <g-typography tag="p" class="g-mb-3">What you can do:</g-typography>

                <g-typography tag="p" class="g-mb-3">
                    <g-icon name="check-circle" color="grey-5" type="solid" />
                    View your balance and transactions
                </g-typography>

                <g-typography tag="p" class="g-mb-3">
                    <g-icon name="check-circle" color="grey-5" type="solid" />
                    Download your statements
                </g-typography>

                <g-typography tag="p" class="g-mb-5">
                    <g-icon name="check-circle" color="grey-5" type="solid" />
                    Manage your account settings
                </g-typography>

                <g-button
                    theme="live"
                    shape="round"
                    size="large"
                    :loading="isLinkLoading"
                    :disabled="isLinkLoading"
                    @click="handleRedirectToDashboard()"
                >
                    Go to GRIN Pay Dashboard
                </g-button>

                <g-divider margin="large" />
            </g-column>
        </g-row>

        <g-row type="flex" justify="space-between" class="g-mb-5">
            <g-column>
                <g-typography tag="h4" font-weight="label">
                    {{ DEFAULT_PAYOUT_METHOD_LABEL_STRING }}
                </g-typography>
            </g-column>

            <g-column>
                <g-switch
                    v-model="isDefault"
                    :loading="isSubmitting"
                    :disabled="isSubmitting"
                    @change="handleUpdateDefault"
                >
                    <g-icon slot="checkedChildren" name="check" size="xsmall" />
                    <g-icon slot="unCheckedChildren" name="times" size="xsmall" />
                </g-switch>
            </g-column>
        </g-row>

        <g-row>
            <g-column>
                <g-typography tag="p" class="g-mb-3">
                    {{ DEFAULT_PAYOUT_METHOD_GRIN_PAY_LINE_1_STRING }}
                </g-typography>

                <g-typography tag="p">
                    {{ DEFAULT_PAYOUT_METHOD_GRIN_PAY_LINE_2_STRING }}
                </g-typography>
            </g-column>
        </g-row>
    </g-drawer>
</template>

<script>
    import { ref, toRefs, watch } from 'vue-demi';
    import { message } from '@grininc/grin-ui';
    import {
        PAYOUT_METHOD_UPDATED_SUCCESS_MESSAGE,
        DEFAULT_PAYOUT_METHOD_LABEL_STRING,
        DEFAULT_PAYOUT_METHOD_GRIN_PAY_LINE_1_STRING,
        DEFAULT_PAYOUT_METHOD_GRIN_PAY_LINE_2_STRING,
        GRIN_CARD_STATUS_CANCELED
    } from '../../../../../app-v2/constants/payouts';
    import { popup } from '../../../../helpers/utils';
    import useToggle from '../../../../composables/use-toggle';
    import usePayoutMethods from '../../../../composables/use-payout-methods';
    import useContactStore from '../../../../../stores/contact';
    import useOnboarding from '../../../../composables/use-onboarding';
    import FullscreenDrawer from "../../../../../app-v2/components/library/FullscreenDrawer";
    import {useFeatureFlagsStore} from "../../../../../stores";

    export default {
        name: 'EditPayoutCardDrawer',
        components: {FullscreenDrawer},
        props: {
            visible: {
                type: Boolean,
                default: false
            },
            payoutAccount: {
                type: Object,
                default: null
            },
            payoutMethod: {
                type: Object,
                default: null
            }
        },

        setup(props, context) {
            const contact = useContactStore().data;
            const featureFlags = useFeatureFlagsStore().data;
            const wrapStyle = {
                position: 'absolute'
            };
            const updated = ref(false);

            const originalIsDefault = () => {
                return props.payoutMethod?.id === props.payoutAccount?.payout_method_id;
            };

            const isDefault = ref(originalIsDefault());

            const { payoutMethod } = toRefs(props);
            watch(payoutMethod, () => {
                isDefault.value = originalIsDefault();
            });

            const handleClose = () => {
                if (updated.value) {
                    context.emit('refresh-payout-account');
                }

                context.emit('close');
            };

            const { createDashboardLink } = useOnboarding();
            const { currentValue: isLinkLoading, setTrue: linkLoading, setFalse: linkDoneLoading } = useToggle(false);

            const handleRedirectToDashboard = async () => {
                linkLoading();
                try {
                    const res = await createDashboardLink(contact.id);
                    const link = res?.data?.url;
                    if (!link) {
                        message.error('Oops! Could not retrieve a dashboard link for managing your card.');
                        linkDoneLoading();
                        return;
                    }
                    openWindow(link);
                } catch (e) {
                    message.error(`Oops! Something went wrong: ${message.responseError(e)}`);
                }
                linkDoneLoading();
            };

            const windowIsOpen = ref(false);
            const openWindow = (link) => {
                const window = popup(link, 480, 640);
                windowIsOpen.value = true;
                setWindowCloseWatcher(window);
            };

            const setWindowCloseWatcher = (window) => {
                const interval = setInterval(() => {
                    if (window.closed) {
                        windowIsOpen.value = false;
                        context.emit('refresh-payout-account', GRIN_CARD_STATUS_CANCELED);
                        clearInterval(interval);
                        handleClose();
                    }
                }, 500);
            };

            const { currentValue: isSubmitting, setTrue: submitting, setFalse: doneSubmitting } = useToggle(false);

            const handleUpdateDefault = (checked) => {
                if (originalIsDefault() === checked) {
                    return;
                }

                submitting();

                const { editPhysicalGrinCard: updateGrinCard } = usePayoutMethods();
                updateGrinCard(contact.id, props.payoutMethod?.id, checked)
                    .then(() => {
                        message.success(PAYOUT_METHOD_UPDATED_SUCCESS_MESSAGE);
                        updated.value = true;
                    })
                    .catch((error) => {
                        message.responseError(error);
                    })
                    .finally(() => {
                        doneSubmitting();
                    });
            };

            return {
                DEFAULT_PAYOUT_METHOD_LABEL_STRING,
                DEFAULT_PAYOUT_METHOD_GRIN_PAY_LINE_1_STRING,
                DEFAULT_PAYOUT_METHOD_GRIN_PAY_LINE_2_STRING,
                wrapStyle,
                isDefault,
                isSubmitting,
                isLinkLoading,
                featureFlags,

                handleClose,
                handleRedirectToDashboard,
                handleUpdateDefault
            };
        }
    };
</script>
